import { InAppState } from "../base/InAppState";
import template from "./InfoTemplate.html";

import { _lcs } from "../../i18n";
import { Phone } from "../../core/Phone";
import { Address } from "../../core/Address";

// Air left above a module scrolled to from the home screen's "Schnellzugriff".
const TARGET_GAP = 18;

/**
 * InfoState — the digital Gästemappe sub-page (`/info`): every visible
 * content block the host maintains in the client-admin, grouped by the
 * platform sections (WLAN, Check-in/-out, Hausordnung, Services, Anreise,
 * Notfall, FAQ …). Reached from the home card and the menu drawer.
 *
 * Rendering per block (uniform block shape, see core-api GuestbookBlock):
 *   • body present            → titled text passage (+ items as a sub-list)
 *   • items only              → header row + one indented row per item
 *   • summary only            → single compact row (title | summary)
 *
 * A filled-in Gästemappe runs to several screens of scrolling, and a guest
 * opening it almost always wants ONE thing — the WLAN password, the check-out
 * time. Each platform section is therefore a collapsed `<details>`, which turns
 * the page into a short list of labelled rows; only the first is open, so the
 * page still reads as content rather than as a menu of empty boxes.
 *
 * Every phone number in the host's text is a `tel:` link (core/Phone), and
 * the emergency numbers sit above all sections as two large buttons — in an
 * emergency nobody should have to find the right accordion, let alone type.
 */
export class InfoState extends InAppState
{
    static ROUTE = "/info";
    static ID = "info";

    showsBottomNav() { return false; }

    async enter()
    {
        // Set by the home screen's "Schnellzugriff": the module to land on.
        const targetBlock = this.routeParams.getQuery( "block", "" ) || null;
        const sections = this._buildSections( targetBlock );

        this.renderScreen( template, {
            house: this._houseName(),
            hasContent: sections.length > 0,
            emergency: this._emergencyNumbers(),
            sections
        } );

        const root = this.app.view.pageContent;

        this.addEventListener(
            root.querySelector( '[data-id="back"]' ),
            "click",
            () => this.app.router.redirect( "/" )
        );

        const toggleAll = root.querySelector( '[data-id="toggle-all"]' );
        if ( toggleAll )
        {
            this.addEventListener( toggleAll, "click", () => this._toggleAll() );

            // `toggle` does not bubble, so the button cannot be kept honest
            // from a delegated listener on the body.
            root.querySelectorAll( ".acc" ).forEach( ( acc ) =>
                this.addEventListener( acc, "toggle", () => this._syncToggleAll() ) );
        }

        if ( targetBlock )
        {
            this._revealBlock( targetBlock );
        }
    }

    /**
     * Scroll the module a quick action pointed at into view. Its section was
     * rendered open (see _buildSections), so this only has to scroll — but not
     * yet: the View scrolls the content slot back to the top on every state
     * change, and that runs once enter() has returned. A zero timeout lands
     * after it. An unknown id (module disabled since) leaves the page as is.
     */
    _revealBlock( blockId )
    {
        const target = Array.from( this.app.view.pageContent.querySelectorAll( "[data-block]" ) )
            .find( ( el ) => el.getAttribute( "data-block" ) === blockId );
        if ( !target )
        {
            return;
        }

        const acc = target.closest( ".acc" );
        if ( acc ) acc.open = true;
        this._syncToggleAll();

        // Scroll the content slot itself, never scrollIntoView(): that scrolls
        // EVERY scrollable ancestor, and the shell's .ga frame — overflow:
        // hidden, but still scrollable from script — slid up with it, leaving
        // an empty strip at the bottom of the device frame.
        this.setTimeout( () =>
        {
            const scroller = this.app.view.pageContent;
            const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top
                + scroller.scrollTop - TARGET_GAP;
            scroller.scrollTo( { top: Math.max( 0, top ), behavior: "smooth" } );
            target.classList.add( "is-target" );
        }, 0 );
    }

    /* ------------------------------- Accordions ------------------------------ */

    _accordions()
    {
        return Array.from( this.app.view.pageContent.querySelectorAll( ".acc" ) );
    }

    /**
     * Open everything, or — once everything is open — close it again.
     *
     * Worth having beyond convenience: it is what makes the whole Gästemappe
     * searchable in one go on the browsers that do not yet expand a closed
     * `<details>` for find-in-page.
     */
    _toggleAll()
    {
        const expand = this._accordions().some( ( acc ) => !acc.open );
        this._accordions().forEach( ( acc ) => { acc.open = expand; } );
        this._syncToggleAll();
    }

    /**
     * The button's label IS its state — it is a command, not a disclosure, so
     * there is no aria-expanded to carry it.
     */
    _syncToggleAll()
    {
        const button = this.app.view.pageContent.querySelector( '[data-id="toggle-all"]' );
        if ( !button )
        {
            return;
        }
        button.textContent = this._accordions().every( ( acc ) => acc.open )
            ? _lcs( "Alle einklappen" )
            : _lcs( "Alle ausklappen" );
    }

    /**
     * Group the visible guestbook blocks into the template's view model:
     * per section a flat `rows` list (compact facts, item rows indented
     * under their block header) and a `texts` list (long-text blocks).
     */
    _buildSections( targetBlock = null )
    {
        // "01814 Bad Schandau": where an address written without a town is
        // searched (core/Address).
        const address = this.app.accommodation?.address || {};
        const locality = [ address.postalCode, address.city ].filter( Boolean ).join( " " );

        return this.app.getGuestbookSections().map( ( sec, index ) =>
        {
            const rows = [];
            const texts = [];

            sec.blocks.forEach( ( block ) =>
            {
                const items = this._valuedItems( block );
                const attachments = this._attachments( block );
                const summary = ( block.summary || "" ).trim();
                const body = ( block.body || "" ).trim();

                // The Wi-Fi block is codes and passwords — a digit string there
                // is never a number to call.
                const dial = block.icon !== "wifi" && !Phone.isExcludedLabel( block.title );
                const parts = ( text, label = "" ) => InfoState._linkParts(
                    text, dial && !Phone.isExcludedLabel( label ), true, locality );

                const metaParts = parts( summary );

                // Downloads need the roomier block layout — a block that has a
                // body OR attachments renders as a titled passage (with its
                // items as a sub-list and a download list beneath).
                if ( body || attachments.length > 0 )
                {
                    texts.push( {
                        blockId: block.id, icon: block.icon, title: block.title,
                        meta: summary, metaParts,
                        bodyParts: InfoState._linkParts( body, dial, false, locality ),
                        items: items.map( ( it ) => ( { ...it, valueParts: parts( it.value, it.label ) } ) ),
                        attachments
                    } );
                    return;
                }

                rows.push( { blockId: block.id, sub: false, icon: block.icon, label: block.title, metaParts } );
                items.forEach( ( it ) => rows.push( { sub: true, icon: "", label: it.label, metaParts: parts( it.value, it.label ) } ) );
            } );

            return {
                title: sec.title,
                // The platform section's own glyph (see core-api
                // GuestbookCatalog.SECTIONS) — a collapsed row is scanned by
                // its icon before its label.
                icon: sec.icon || "circle",
                // Only the first: an all-collapsed page reads as if the host
                // had filled in nothing. Opened from a quick action, only the
                // section holding that module (see _revealBlock).
                isOpen: targetBlock ? sec.blocks.some( ( b ) => b.id === targetBlock ) : index === 0,
                rows,
                texts
            };
        } );
    }

    /**
     * A text as render parts: [ { text } | { text, tel, pill } | { text, map } ].
     * Addresses become Google Maps links (core/Address), phone numbers tel:
     * links (core/Phone). `pill` marks a value that is nothing but the number
     * — it renders as a large button rather than as a link in a sentence.
     *
     * @param {boolean} open      A normal text. False around secrets and codes
     *                            (Wi-Fi, key safe): no addresses there, and only
     *                            numbers in the international form.
     * @param {boolean} whole     The text is one value of its own (see Phone.split).
     * @param {string} locality   The house's "postcode town" (see Address.split).
     */
    static _linkParts( text, open = true, whole = true, locality = "" )
    {
        if ( !text )
        {
            return [];
        }

        const pieces = open ? Address.split( text, locality ) : [ { text } ];
        const parts = [];
        pieces.forEach( ( piece ) =>
        {
            if ( piece.map )
            {
                parts.push( piece );
                return;
            }
            Phone.split( piece.text, whole && pieces.length === 1, !open ).forEach( ( p ) => parts.push( p ) );
        } );

        const pill = whole && Phone.isOnlyNumber( parts );
        return parts.map( ( p ) => ( p.tel ? { ...p, pill } : p ) );
    }

    /**
     * The two numbers every guest needs without looking: 112 is the EU-wide
     * emergency number (fire brigade and ambulance), 110 the German police.
     * Outside Germany the police number differs, so 112 stands alone there —
     * it reaches the police everywhere in Europe as well.
     */
    _emergencyNumbers()
    {
        const country = String( this.app.accommodation?.address?.countryCode || "DE" ).toUpperCase();
        const numbers = [ { tel: "112", label: _lcs( "Feuerwehr & Rettungsdienst" ) } ];
        if ( country === "DE" )
        {
            numbers.push( { tel: "110", label: _lcs( "Polizei" ) } );
        }
        return numbers;
    }

    _valuedItems( block )
    {
        return ( Array.isArray( block.items ) ? block.items : [] )
            .map( ( it ) => ( { label: ( it.label || "" ).trim(), value: ( it.value || "" ).trim() } ) )
            .filter( ( it ) => it.label && it.value );
    }

    // Downloads the host uploaded for this module: an icon key (image vs.
    // document), the guest-facing label and the public file URL.
    _attachments( block )
    {
        return ( Array.isArray( block.attachments ) ? block.attachments : [] )
            .filter( ( a ) => a && a.url )
            .map( ( a ) => ( {
                url: a.url,
                name: ( a.name || _lcs( "Download" ) ).trim(),
                icon: ( typeof a.mimeType === "string" && a.mimeType.indexOf( "image/" ) === 0 ) ? "image" : "file-text"
            } ) );
    }

    _houseName()
    {
        // The resolved accommodation is the source of truth; fall back to a
        // name derived from the subdomain slug (pure demo mode).
        if ( this.app.accommodation?.name )
        {
            return this.app.accommodation.name;
        }

        const slug = this.app.theme.getTenantSlug();
        if ( !slug )
        {
            return "Gästehaus Felsenblick";
        }
        return slug
            .split( "-" )
            .map( ( w ) => w.charAt( 0 ).toUpperCase() + w.slice( 1 ) )
            .join( " " );
    }
}
